/**
 * The Region choices offered on System Manager -> Company Details -> Basic
 * Details, and the currency each one implies.
 *
 * **Why a literal table rather than the `locale-currency` package the
 * architecture doc suggests.** The doc's warning is against hand-rolling the
 * *full* locale -> ISO 4217 mapping, which is long, drifts, and is exactly what
 * a package is for. That is not what this is: the Region select is a curated,
 * fixed list, so the mapping has one row per offered region and lives in the
 * same literal as the list itself — it cannot fall out of step with the
 * dropdown, because it *is* the dropdown. Adding a region is one line here.
 *
 * If the region list ever opens up to the full BCP 47 space, drop this table
 * and take the dependency (`getCurrency("en-GB") === "GBP"`); nothing outside
 * this module needs to change, because callers only use the two helpers below.
 */

export type CompanyRegion = {
  /** BCP 47 tag, stored as `Region` on the company profile. */
  value: string;
  label: string;
  /** ISO 4217 code this region implies. */
  currency: string;
};

/** The value the Region select uses for "None" — stored as `null`. */
export const NO_REGION = "";

/**
 * The region used for formatting when none is configured. Formatting always
 * needs *a* locale; "None" means "nothing chosen", not "no separators".
 */
export const DEFAULT_REGION = "en-GB";
export const DEFAULT_CURRENCY = "GBP";

export const COMPANY_REGIONS: readonly CompanyRegion[] = [
  { value: "en-GB", label: "English (United Kingdom)", currency: "GBP" },
];

/** The locale actually used for formatting — "None" falls back to en-GB. */
export const effectiveRegion = (region: string | null | undefined): string =>
  region || DEFAULT_REGION;

/** The ISO 4217 code a region implies. Unknown/None regions imply Sterling. */
export const currencyForRegion = (region: string | null | undefined): string =>
  COMPANY_REGIONS.find((r) => r.value === region)?.currency ?? DEFAULT_CURRENCY;

export const regionLabel = (region: string | null | undefined): string =>
  COMPANY_REGIONS.find((r) => r.value === region)?.label ?? "None";

/**
 * The symbol a region renders a currency with — GBP is "£" in en-GB but "£GB"
 * in some locales, so it is asked for rather than assumed.
 */
export const currencySymbol = (region: string | null | undefined, currency: string): string => {
  try {
    const parts = new Intl.NumberFormat(effectiveRegion(region), {
      style: "currency",
      currency,
    }).formatToParts(1);
    return parts.find((p) => p.type === "currency")?.value ?? currency;
  } catch {
    return currency;
  }
};

/** "GBP (£)" — how the derived currency is labelled in the UI. */
export const currencyDisplayLabel = (
  region: string | null | undefined,
  currency: string
): string => `${currency} (${currencySymbol(region, currency)})`;
